import { useCustom, useTranslate } from '@refinedev/core';
import { Table, Tag, Typography } from 'antd';
import { format, formatDuration, intervalToDuration, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { REVIEW_SESSION_FIELDS } from '../../components/layout/ReviewSessionControl';
import { ReviewSession } from '../../schema/be-graphql-generated';

const { Title, Text } = Typography;

/**
 * E04 · ReviewSessions on the Client Activity Log (built 2026-09-21) — the board's
 * `Client Activity Log (review sessions)`: "the client's review sessions (active + past) fed by
 * ReviewSessions … active entry shows startedAt + a running duration (endedAt empty); past
 * entries show startedAt–endedAt, endedVia and the COMPUTED duration (endedAt − startedAt)."
 *
 * The rows are the read model's (`reviewSessions(subject)`, newest first, the caller's practice);
 * a running bracket's duration ticks locally every 15 s. Re-read every 30 s so an End from the
 * header — or the idle timeout — lands here without a reload.
 */
const durationOf = (
  startedAt: string,
  endedAt: string | null | undefined,
  locale: 'en' | 'ru',
): string =>
  formatDuration(
    intervalToDuration({
      start: parseISO(startedAt),
      end: endedAt ? parseISO(endedAt) : new Date(),
    }),
    {
      format: ['days', 'hours', 'minutes'],
      locale: locale === 'ru' ? ru : enUS,
    },
  ) || (locale === 'ru' ? '< 1 мин' : '< 1 min');

export const ReviewSessionsSection: React.FC<{
  clientId: string | undefined;
  locale: 'en' | 'ru';
}> = ({ clientId, locale }) => {
  const t = useTranslate();
  const { data, isLoading } = useCustom<ReviewSession[]>({
    url: '',
    method: 'get',
    meta: {
      operation: 'reviewSessions',
      fields: [...REVIEW_SESSION_FIELDS],
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
      },
    },
    queryOptions: {
      enabled: Boolean(clientId),
      refetchInterval: 30_000,
      // the periodic re-read re-renders the page only when the rows actually changed — the hub
      // holds modals with pickers, and a needless re-render under an open panel is a flake
      notifyOnChangeProps: ['data'],
    },
  });
  const rows = useMemo(() => data?.data ?? [], [data]);

  // the running bracket's duration ticks
  const hasActive = rows.some((r) => !r.endedAt);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hasActive) return undefined;
    const handle = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(handle);
  }, [hasActive]);

  const stamp = (iso: string) =>
    format(
      parseISO(iso),
      locale === 'ru' ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd hh:mm a',
    );

  return (
    <div data-testid="cc-client-show-review-sessions">
      <Title level={4}>{t('pages.clientsShow.reviewSessions.title')}</Title>
      <Table<ReviewSession>
        size="small"
        rowKey="reviewSessionId"
        loading={Boolean(clientId) && isLoading}
        dataSource={rows}
        pagination={rows.length > 10 ? { pageSize: 10 } : false}
        locale={{
          emptyText: (
            <Text type="secondary">
              {t('pages.clientsShow.reviewSessions.none')}
            </Text>
          ),
        }}
        onRow={(r) =>
          ({
            'data-testid': `cc-review-session-row-${
              r.endedAt ? 'past' : 'active'
            }`,
            'data-review-session-id': r.reviewSessionId,
          } as React.HTMLAttributes<HTMLElement>)
        }
        columns={[
          {
            title: t('pages.clientsShow.reviewSessions.startedAt'),
            dataIndex: 'startedAt',
            render: (v: string) => stamp(v),
          },
          {
            title: t('pages.clientsShow.reviewSessions.endedAt'),
            dataIndex: 'endedAt',
            render: (v: string | null) =>
              v ? (
                stamp(v)
              ) : (
                <Tag color="processing">
                  {t('pages.clientsShow.reviewSessions.active')}
                </Tag>
              ),
          },
          {
            title: t('pages.clientsShow.reviewSessions.duration'),
            key: 'duration',
            render: (_: unknown, r: ReviewSession) =>
              durationOf(r.startedAt, r.endedAt, locale),
          },
          {
            title: t('pages.clientsShow.reviewSessions.endedVia'),
            dataIndex: 'endedVia',
            render: (v: string | null) =>
              v === 'explicit'
                ? t('pages.clientsShow.reviewSessions.explicit')
                : v === 'idle-timeout'
                ? t('pages.clientsShow.reviewSessions.idleTimeout')
                : '—',
          },
          {
            title: t('pages.clientsShow.reviewSessions.by'),
            dataIndex: 'performer',
            // R115 (the founder, 2026-09-23: "the By column with ID there is extremely hard for
            // humans to interpret") — the therapist's name from the server (read time), the id beside
            // it; a member whose row is gone keeps the bare id
            render: (v: string, r: ReviewSession) => {
              const key = v.replace('users/', '');
              return r.performerName ? `${r.performerName} · ${key}` : key;
            },
          },
        ]}
      />
    </div>
  );
};
